// src/components/FatwaCard/FatwaCard.tsx
import React, { useRef, useState } from 'react';
import { downloadCardAsImage } from '../../utils/downloadUtils';
import './FatwaCard.css';

interface Fatwa {
  id: string;
  question: string;
  reponse: string;
  reference: string;
  tags: string[];
}

interface FatwaCardProps {
  fatwa: Fatwa;
  chapitreTitre: string;
  onTagClick: (tag: string) => void;
}

const FatwaCard: React.FC<FatwaCardProps> = ({ fatwa, chapitreTitre, onTagClick }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);
    try {
      await downloadCardAsImage(cardRef.current, {
        fileName: `fatwa-${fatwa.id}`,
        scale: 2,
        watermark: {
          logoPath: '/images/logoP&F.jpg',
          text: 'Traduit par Mâhi Bâyân'
        }
      });
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Fonction pour formater la réponse avec les débuts de citations en gras
  const formatReponse = (text: string) => {
    const patterns = [
      /(Cheykh Souleymane El-Rouheylî dit :)/g,
      /(L'avis prépondérant.*? est que)/g,
      /(Il est préférable.*?)/g,
      /(Selon la majorité des savants)/g,
      /(L'avis correct est que)/g,
      /(C'est l'avis que je considère comme prépondérant)/g,
      /(Je dis :)/g,
      /(Nous disons :)/g,
      /(La réponse :)/g,
    ];

    let formattedText = text;
    
    patterns.forEach(pattern => {
      formattedText = formattedText.replace(pattern, '<strong>$1</strong>');
    });

    // Convertir les \n\n en paragraphes HTML
    formattedText = formattedText.replace(/\n\n/g, '</p><p>');
    formattedText = '<p>' + formattedText + '</p>';

    return formattedText;
  };

  // Tronquer la réponse si elle est longue
  const shouldTruncate = fatwa.reponse.length > 400;
  const displayText = !isExpanded && shouldTruncate 
    ? fatwa.reponse.substring(0, 400) + '...'
    : fatwa.reponse;

  return (
    <div ref={cardRef} className="fatwa-card">
      <div className="fatwa-header">
        <div className="fatwa-number">Q{fatwa.id.replace('q', '')}</div>
        <div className="fatwa-chapter">{chapitreTitre}</div>
      </div>

      <div className="fatwa-question">
        <h3 className="question-title">Question :</h3>
        <p className="question-text">{fatwa.question}</p>
      </div>

      <div className="fatwa-reponse">
        <h3 className="reponse-title">Réponse :</h3>
        <div 
          className="reponse-text"
          dangerouslySetInnerHTML={{ 
            __html: formatReponse(displayText) 
          }}
        />
        
        {shouldTruncate && (
          <button 
            className="expand-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Voir moins' : 'Lire la suite'}
          </button>
        )}
      </div>

      <div className="fatwa-reference">
        <strong>Référence :</strong> {fatwa.reference}
      </div>

      {fatwa.tags.length > 0 && (
        <div className="fatwa-tags">
          <strong>Mots-clés :</strong>
          <div className="tags-container">
            {fatwa.tags.map((tag, index) => (
              <button
                key={index}
                className="tag-chip"
                onClick={() => onTagClick(tag)}
                title={`Rechercher les fatwas sur "${tag}"`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        className="download-button"
        onClick={handleDownload}
        disabled={isDownloading}
        aria-label="Télécharger cette fatwa"
      >
        {isDownloading ? (
          <>
            <span className="loading-spinner">⏳</span>
            Téléchargement...
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path 
                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <polyline 
                points="7 10 12 15 17 10" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <line 
                x1="12" 
                y1="15" 
                x2="12" 
                y2="3" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
            Télécharger
          </>
        )}
      </button>
    </div>
  );
};

export default FatwaCard;